// src/components/Issues/AssignDialog.js
import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Button,
  CircularProgress,
  TextField,
  Box,
} from '@mui/material';
import { Autocomplete } from '@mui/material';

const AssignDialog = ({
  open,
  selectedIssue,
  selectedUser,
  usersList,
  processingId,
  onClose,
  onUserChange,
  onAssign,
}) => {
  const [searchInput, setSearchInput] = useState('');

  // Filter users based on search input
  const filteredUsers = useMemo(() => {
    if (!searchInput) return usersList;
    
    const searchTerm = searchInput.toLowerCase();
    return usersList.filter(user => 
      user.name?.toLowerCase().includes(searchTerm) ||
      user.email?.toLowerCase().includes(searchTerm)
    );
  }, [usersList, searchInput]);

  // Get the selected user object
  const getSelectedUserObject = () => {
    return usersList.find(user => user.id === selectedUser) || null;
  };

  const handleUserChange = (event, value) => {
    onUserChange(value ? value.id : '');
  };

  const handleInputChange = (event, value) => {
    setSearchInput(value);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Assign Issue to User</DialogTitle>
      <DialogContent>
        <FormControl fullWidth sx={{ mt: 2 }}>
          <Autocomplete
            options={filteredUsers}
            getOptionLabel={(option) => option.name || 'Unnamed User'}
            value={getSelectedUserObject()}
            onChange={handleUserChange}
            onInputChange={handleInputChange}
            inputValue={searchInput}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search and Select User"
                placeholder="Type to search users..."
                variant="outlined"
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id}>
                <Box>
                  <Box sx={{ fontWeight: 500 }}>{option.name}</Box>
                  {option.email && (
                    <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                      {option.email}
                    </Box>
                  )}
                </Box>
              </Box>
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText="No users found"
            loading={usersList.length === 0}
          />
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={onAssign}
          variant="contained"
          disabled={!selectedUser || processingId === selectedIssue?.id}
          startIcon={processingId === selectedIssue?.id ? <CircularProgress size={20} /> : null}
        >
          Assign
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignDialog;